import { createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { MainLayout } from '@/components/layout/MainLayout';

// Pages (lazy loaded with auto-reload on chunk failure)
import { lazy } from 'react';

/** Wrap lazy() to auto-reload when a chunk is missing after a deploy */
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch((err: any) => {
      // Chunk missing after deploy — reload once
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      throw err;
    })
  );
}

const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const CallbackPage = lazyWithRetry(() => import('@/pages/CallbackPage'));
const DashboardPage = lazyWithRetry(() => import('@/features/dashboard/MaxedOutDashboardPage'));
const QueuePage = lazyWithRetry(() => import('@/features/matchmaking/QueuePageMaxedOut'));
const GathersPage = lazyWithRetry(() => import('@/features/gathers/GathersPageEnhanced'));
const LadderPage = lazyWithRetry(() => import('@/features/ladder/LadderPage'));
const TournamentsPage = lazyWithRetry(() => import('@/features/tournaments/TournamentsPageMaxedOut'));
const ChatPage = lazyWithRetry(() => import('@/features/chat/ChatPage'));
const ChatEnhancedPage = lazyWithRetry(() => import('@/features/chat/ChatEnhancedPage'));
const FriendsPage = lazyWithRetry(() => import('@/features/friends/FriendsPage'));
const VirtualLANPage = lazyWithRetry(() => import('@/features/virtual-lan/VirtualLANPage'));
const ProfilePage = lazyWithRetry(() => import('@/features/profile/ProfilePage'));
const GatherMatchLobbyWindow = lazyWithRetry(() => import('@/pages/GatherMatchLobbyWindow'));

// New MAXED OUT pages
const TeamsPage = lazyWithRetry(() => import('@/features/teams/TeamsPage'));
const ChallengesPage = lazyWithRetry(() => import('@/features/challenges/ChallengesPage'));
const LeaderboardsPage = lazyWithRetry(() => import('@/features/leaderboards/LeaderboardsPage'));
const NewsPage = lazyWithRetry(() => import('@/features/news/NewsPage'));
const AdvancedStatsPage = lazyWithRetry(() => import('@/features/stats/AdvancedStatsPage'));
const MatchHistoryPage = lazyWithRetry(() => import('@/features/matches/MatchHistoryPage'));

// League of Legends
const LoLProfilePage = lazyWithRetry(() => import('@/features/lol/LoLProfilePage'));

// Game portals (dedicated pages)
const CS2Page = lazyWithRetry(() => import('@/features/cs2/CS2Page'));
const Dota2Page = lazyWithRetry(() => import('@/features/dota2/Dota2Page'));
const FortnitePage = lazyWithRetry(() => import('@/features/fortnite/FortnitePage'));
const EpicGamesPage = lazyWithRetry(() => import('@/features/epic/EpicGamesPage'));
const MinecraftPage = lazyWithRetry(() => import('@/features/minecraft/MinecraftPage'));

// Portals
const PortalsOverviewPage = lazyWithRetry(() => import('@/features/portals/PortalsOverviewPage'));
const PortalPage = lazyWithRetry(() => import('@/features/portals/PortalPage'));
const PUBGPortalPage = lazyWithRetry(() => import('@/features/portals/PUBGPortalPage'));

// Racing
const RacingPortalPage = lazyWithRetry(() => import('@/features/racing/RacingPortalPage'));

// Streams / Twitch
const StreamsPage = lazyWithRetry(() => import('@/features/streams/StreamsPage'));

// Stronghold
const StrongholdLadderPage = lazyWithRetry(() => import('@/features/stronghold/StrongholdLadderPage'));
const StrongholdAdminDashboard = lazyWithRetry(() => import('@/features/admin/stronghold/StrongholdAdminDashboard'));

// Determine base path from current location
// If app is served from /client/, use that as base
const isClientPath = window.location.pathname.startsWith('/client');
const basename = isClientPath ? '/client' : '/';

export const router = createBrowserRouter([
  // Auth routes
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/auth/callback',
    element: <CallbackPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/client/auth/callback',
    element: <CallbackPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/gather-lobby/:gatherId',
    element: <GatherMatchLobbyWindow />,
    errorElement: <ErrorBoundary />,
  },

  // Main app routes (under root /)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'queue',
        element: <QueuePage />,
      },
      {
        path: 'gathers',
        element: <GathersPage />,
      },
      {
        path: 'ladder',
        element: <LadderPage />,
      },
      {
        path: 'tournaments',
        element: <TournamentsPage />,
      },
      {
        path: 'chat',
        element: <ChatEnhancedPage />,
      },
      {
        path: 'friends',
        element: <FriendsPage />,
      },
      {
        path: 'lan',
        element: <VirtualLANPage />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'profile/:userId',
        element: <ProfilePage />,
      },
      {
        path: 'teams',
        element: <TeamsPage />,
      },
      {
        path: 'challenges',
        element: <ChallengesPage />,
      },
      {
        path: 'leaderboards',
        element: <LeaderboardsPage />,
      },
      {
        path: 'news',
        element: <NewsPage />,
      },
      {
        path: 'stats',
        element: <AdvancedStatsPage />,
      },
      {
        path: 'matches',
        element: <MatchHistoryPage />,
      },
      {
        path: 'matchmaking',
        element: <QueuePage />,
      },
      {
        path: 'portals',
        element: <PortalsOverviewPage />,
      },
      {
        path: 'portals/lol',
        element: <LoLProfilePage />,
      },
      {
        path: 'portals/riot',
        element: <LoLProfilePage />,
      },
      {
        path: 'portals/cs2',
        element: <CS2Page />,
      },
      {
        path: 'portals/dota2',
        element: <Dota2Page />,
      },
      {
        path: 'portals/fortnite',
        element: <FortnitePage />,
      },
      {
        path: 'portals/epic',
        element: <EpicGamesPage />,
      },
      {
        path: 'portals/minecraft',
        element: <MinecraftPage />,
      },
      {
        path: 'portals/pubg',
        element: <PUBGPortalPage />,
      },
      {
        path: 'portals/racing',
        element: <RacingPortalPage />,
      },
      {
        path: 'portals/twitch',
        element: <StreamsPage />,
      },
      {
        path: 'portals/:portalId',
        element: <PortalPage />,
      },
      {
        path: 'stronghold',
        element: <StrongholdLadderPage />,
      },
    ],
  },

  // Admin routes (under /admin)
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'stronghold',
        element: <StrongholdAdminDashboard />,
      },
    ],
  },
], {
  basename,
});
